import { ThemeNames, createAppTheme } from "@diana/mui";
import {
  Box,
  CssBaseline,
  ThemeProvider,
  Typography,
  useTheme,
} from "@mui/material";
import { SnackbarProvider } from "notistack";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { SystemStateTracker } from "./components/SystemStateTracker";
import { setupI18n } from "./i18n";
import { GENERAL_NS } from "./i18n/general";
import { resources } from "./i18n/resources";
import { LoginPage } from "./pages/LoginPage/LoginPage";
import { useThemeStore } from "./store/useThemeStore";
import { LocalStorageKey, localStorageUtil } from "./utils/localStorage";

setupI18n({ resources });

const Bootstrap = () => {
  const { palette } = useTheme();
  const { t } = useTranslation(GENERAL_NS);

  const currentYear = useMemo(() => {
    const currentDate = new Date();
    return currentDate.getFullYear();
  }, []);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexGrow: 1,
          alignSelf: "center",
          p: 4,
        }}
      >
        <LoginPage />
      </Box>
      <Box
        sx={{
          width: "100%",
          textAlign: "center",
          position: "relative",
          marginTop: "auto",
          pb: 4,
        }}
      >
        <Typography
          sx={{
            color: palette.text.secondary,
            mb: 0.5,
          }}
        >
          {t("copyright", { year: currentYear })}
        </Typography>
        <Typography
          sx={{
            color: palette.text.secondary,
          }}
        >
          {t("privacyPolicy")}
        </Typography>
      </Box>
    </Box>
  );
};

export const LoginLayout = () => {
  const lightTheme = "dianaLight";
  const themeName = useThemeStore((store) => store.themeName);
  const setTheme = useThemeStore((store) => store.setTheme);

  const primaryColor = "#1d82c0";

  const theme = useMemo(
    () => createAppTheme({ themeName, primaryColor }),
    [primaryColor, themeName]
  );

  useEffect(() => {
    const localStorageTheme = localStorageUtil.getItem({
      key: LocalStorageKey.defaultTheme,
    });
    if (!localStorageTheme) {
      setTheme(lightTheme);
    }
  }, [setTheme]);

  useEffect(() => {
    localStorageUtil.setItem({
      key: LocalStorageKey.defaultTheme,
      value: themeName,
    });
    updateDocumentColorScheme(themeName);
  }, [themeName]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SystemStateTracker />
      <Bootstrap />
    </ThemeProvider>
  );
};

const updateDocumentColorScheme = (theme: ThemeNames) => {
  const colorScheme = theme === "dianaDark" ? "dark" : "light";
  
  const meta = document.querySelector('meta[name="color-scheme"]') as HTMLMetaElement;
  if (meta !== null) {
    meta.content = colorScheme;
  }

  document.documentElement.style.colorScheme = colorScheme;
};

const router = createBrowserRouter([
  {
    path: "/*",
    element: <LoginLayout />,
  },
]);

function App() {
  return (
    <SnackbarProvider anchorOrigin={{ vertical: 'top', horizontal: 'right' }} maxSnack={3}>
        <RouterProvider router={router} />
    </SnackbarProvider>
  );
}

export default App;
